/**-----------------------------------------------------------------------------
 *                                    E2RH
 * -----------------------------------------------------------------------------
 *
 *  filename: migraPaye.js
 *
 *  author: Thomas CLERET (thomas.cleret@proton.me)
 *
 *  creation: 26/9/2025 - 14:26
 *
 *  description:
 *
 *  Copyright (c) 2025 E2RH
 * -----------------------------------------------------------------------------
 */

import * as React from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import * as utils from "../utils/utils.tsx";
import DataTable from "react-data-table-component";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";

/**
 * Interface for the employee search in Migrapaye
 *
 * @param {list} domainsList the authorized domains list
 */
export default function SearchEmployee({ domainsList }) {
   const searchDom = "searchDom";
   const searchItem = "searchItem";
   const maxStdoutSize = 1000;
   const [selectedValue, setSelectedValue] = React.useState(domainsList.length > 0 ? domainsList[0] : "");
   const [searchReply, setSearchReply] = React.useState("");
   const [domainsSelect, setDomainsSelect] = React.useState([]);
   const [searchType, setSearchType] = React.useState([]);

   let stdout = "";

   // Populate the domains selector
   if (domainsSelect.length < 1) {
      const _domainsSelect = [];
      for (let i = 0; i < domainsList.length; i++) {
         _domainsSelect.push(
            <MenuItem key={i} value={domainsList[i]} selected={i === 0}>
               {domainsList[i]}
            </MenuItem>
         );
      }
      setDomainsSelect(_domainsSelect);
   }

   const delay = (ms) => new Promise((res) => setTimeout(res, ms));

   /**
    * Send a search request to the backend, and wait for reply
    *
    * @param event The event that triggered the search
    */
   async function handleSearch(event) {
      if (event != null) {
         event.preventDefault();
      }

      let item = "";
      let domain = "";
      if (document.getElementById(searchItem) != null && document.getElementById(searchDom) != null) {
         item = document.getElementById(searchItem).value;
         domain = document.getElementById(searchDom).textContent;
      } else {
         return;
      }

      let empl_name = "";
      let empl_no_secu = "";
      if (searchType == "name") {
         empl_name = item;
      } else {
         empl_no_secu = item;
      }

      // Send the requests: firt to launch the script, then in a loop to get the status until script is done
      try {
         let reply = await utils.apiClient.post("/migrapaye/searchEmployee", {
            domain: domain,
            empl_name: empl_name,
            empl_no_secu: empl_no_secu,
         });

         const script_id = reply.data.script_id;
         let isRunning = true;

         // String display while running
         while (isRunning) {
            await delay(500);
            reply = await utils.apiClient.post("/migrapaye/getStatus", { script_id: script_id });

            isRunning = reply.data.isRunning;
            for (const line of reply.data.stdout) {
               stdout += line + "\n";
            }
            for (const line of reply.data.stderr) {
               stdout += line + "\n";
            }
            setSearchReply(
               <div
                  className="migrapaye_search_info"
                  dangerouslySetInnerHTML={{ __html: utils.formatString(stdout) }}
               ></div>
            );
         }

         // Table display when done
         let display_stdout = "";
         const search_results = [];
         let header_found = false;
         for (let i = 0; i < stdout.split("\n").length; i++) {
            const line = stdout.split("\n")[i];
            if (line[0] != "#") {
               if (i == 0) display_stdout += line;
               else display_stdout += "\n" + line;
            } else {
               if (!header_found) header_found = true;
               else search_results.push(line.substring(1).split(";"));
            }
         }
         const columns_cfg = [
            { name: "Dossier", selector: (row) => row[0], sortable: true, width: "120px", grow: 0 },
            { name: "Matricule", selector: (row) => row[1], sortable: true, width: "120px", grow: 0 },
            { name: "N° Sécu", selector: (row) => row[2], sortable: true, width: "150px", grow: 0 },
            { name: "Nom", selector: (row) => row[3], sortable: true, grow: 0.5 },
            { name: "Prénom", selector: (row) => row[4], sortable: true, grow: 0.5 },
            { name: "N° Contrat", selector: (row) => row[5], sortable: true, width: "100px", grow: 0 },
            { name: "CCN", selector: (row) => row[6], sortable: true, width: "100px", grow: 0 },
            { name: "IDCC", selector: (row) => row[7], sortable: true, width: "100px", grow: 0 },
            { name: "Emploi conventionnel", selector: (row) => row[8], sortable: true, width: "200px", grow: 0 },
            { name: "Nature emploi", selector: (row) => row[9], sortable: true, grow: 1 },
         ];
         const pagination_cfg = {
            rowsPerPageText: "Salariés par page",
            rangeSeparatorText: "sur",
            selectAllRowsItem: true,
            selectAllRowsItemText: "Tous",
         };

         setSearchReply(
            <div>
               <div
                  className="migrapaye_search_info"
                  dangerouslySetInnerHTML={{ __html: utils.formatString(display_stdout) }}
               ></div>
               <DataTable
                  columns={columns_cfg}
                  data={search_results}
                  highlightOnHover
                  striped
                  dense
                  pagination
                  paginationComponentOptions={pagination_cfg}
               />
               <div className="migrapaye_actions">
                  <button className="table-button-add" key="export" onClick={() => exportCsv()}>
                     {" "}
                     Export CSV
                  </button>
                  <button className="table-button-delete" key="refresh" onClick={() => saveDatabase()}>
                     {" "}
                     Rafraichir base
                  </button>
               </div>
            </div>
         );
      } catch (error) {
         toast.error(error);
      }
   }

   /**
    * Export the table to a CSV file
    */
   function exportCsv() {
      const saveFile = async (blob) => {
         const a = document.createElement("a");
         a.download = "recherche_salaries.csv";
         a.href = URL.createObjectURL(blob);
         a.addEventListener("click", () => {
            setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
         });
         a.click();
      };

      let csv_content = "\ufeff";
      for (const line of stdout.split("\n")) {
         if (line[0] == "#") {
            csv_content += line.substring(1) + "\n";
         }
      }

      const blob = new Blob([csv_content], { type: "text/csv" });
      saveFile(blob);
   }

   /**
    * Save database logic
    */
   async function saveDatabase() {
      // Send the requests: firt to launch the script, then in a loop to get the status until script is done
      try {
         let reply = await utils.apiClient.post("/migrapaye/saveDatabase");

         const script_id = reply.data.script_id;
         let isRunning = true;

         const stdout_array = [];

         while (isRunning) {
            await delay(200);
            reply = await utils.apiClient.post("/migrapaye/getStatus", { script_id: script_id });

            isRunning = reply.data.isRunning;

            // Fill the limited-size buffer to display stdout history
            for (const line of reply.data.stdout) {
               stdout_array.push(line);
            }
            if (stdout_array.length > maxStdoutSize) {
               stdout_array.splice(0, stdout_array.length - maxStdoutSize);
            }
            stdout = "";
            for (const line of stdout_array) {
               stdout += "\n" + line;
            }
            setSearchReply(
               <div style={{ height: "calc(100vh - 350px) !important" }}>
                  <div
                     className="migrapaye_raw_result"
                     id="scrollable_text"
                     dangerouslySetInnerHTML={{ __html: utils.formatString(stdout) }}
                  ></div>
               </div>
               // <div className="migrapaye_raw_result" id="scrollable_text">
               //    {utils.formatString(stdout)}
               // </div>
            );
            const text = document.getElementById("scrollable_text");
            if (text) {
               text.scrollTop = text.scrollHeight;
            }
         }
         setSearchReply(
            <div>
               <div style={{ height: "calc(100vh - 350px) !important" }}>
                  <div className="migrapaye_raw_result">{stdout}</div>
               </div>
               <div className="migrapaye_actions">
                  <button className="table-button-add" key="export" onClick={() => exportCsv()}>
                     {" "}
                     Export CSV
                  </button>
                  <button className="table-button-delete" key="refresh" onClick={() => saveDatabase()}>
                     {" "}
                     Rafraichir base
                  </button>
               </div>
            </div>
         );
      } catch (error) {
         toast.error(error);
      }
   }

   return (
      <div className="action_content">
         <h3>Recherche salariés</h3>
         <form className="flex space-x-3" style={{ paddingTop: "20px" }} onSubmit={(event) => handleSearch(event)}>
            <Grid
               container
               spacing={0}
               direction="row"
               alignItems="center"
               justifyContent="center"
               style={{ width: "100%" }}
            >
               <Grid item style={{ width: "25%" }}>
                  <FormControl fullWidth>
                     <FormLabel> Type de recherche</FormLabel>
                     <RadioGroup
                        defaultValue="name"
                        onChange={(event) => setSearchType(event.target.value)}
                        value={searchType}
                     >
                        <FormControlLabel value="no_secu" control={<Radio />} label="Numéro Sécurité Sociale" />
                        <FormControlLabel value="name" control={<Radio />} label="Nom" />
                     </RadioGroup>
                  </FormControl>
               </Grid>
               <Grid item style={{ width: "25%" }}>
                  <FormControl fullWidth>
                     <InputLabel> Domaine </InputLabel>
                     <Select
                        id={searchDom}
                        style={{ marginRight: "10px" }}
                        value={selectedValue}
                        onChange={(event) => setSelectedValue(event.target.value)}
                        label="Type de recherche"
                     >
                        {domainsSelect}
                     </Select>
                  </FormControl>
               </Grid>
               <Grid item style={{ width: "25%" }}>
                  <FormControl fullWidth>
                     <TextField id={searchItem} label="Critère" />
                  </FormControl>
               </Grid>
               <Grid item style={{ width: "25%" }}>
                  <FormControl fullWidth>
                     <Button
                        variant="contained"
                        type="submit"
                        className="table-button-edit"
                        size="large"
                        startIcon={<SearchIcon />}
                     >
                        Rechercher
                     </Button>
                  </FormControl>
               </Grid>
            </Grid>
         </form>
         {searchReply}
      </div>
   );
}

SearchEmployee.propTypes = {
   domainsList: PropTypes.arrayOf(PropTypes.string).isRequired,
};
